import React, { useState, useEffect, useContext } from 'react';
import Button from "app/components/CustomButtons/Button.js";
import axios from 'axios';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { PackageContext } from 'app/context/PackageContext';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { CargoMode } from "app/types/enums";

export default function PackageTable(props) {
    const { cargoMode, primaryTransportMode, backgroundColor } = props;
    const newPackage = {
        packageCount: 0,
        type: 0,
        description: "",
        grossweight: 0,
        volume: 0,
        length: 0,
        width: 0,
        height: 0,
        stackMultiplier: 0,
        measureUnitsGrossWeight: "kg",
        measureUnitsLength: "cm",
        measureUnitsWidth: "cm",
        measureUnitsHeight: "cm",
        postType: "",
        packageCountValidation: true,
        typeValidation: true,
        descriptionValidation: true,
        grossweightValidation: true,
        volumeValidation: true,
        lengthValidation: true,
        widthValidation: true,
        heightValidation: true,
        passValidation: true

    };
    const [packageTypes, setPackageTypes] = useState([]);
    const [packages, setPackages] = useState([newPackage]);
    const [measureUnitsWeight, setMeasureUnitsWeight] = useState([]);
    const [measureUnitsLength, setMeasureUnitsLength] = useState([]);
    const [measureUnitsWidth, setMeasureUnitsWidth] = useState([]);
    const [measureUnitsHeight, setMeasureUnitsHeight] = useState([]);
    const [selectedWeightUnit, setSelectedWeightUnit] = useState({ "id": "kg", "label": "kg" });
    const [selectedLengthUnit, setSelectedLengthUnit] = useState({ "id": "cm", "label": "cm" });
    const [selectedWidthUnit, setSelectedWidthUnit] = useState({ "id": "cm", "label": "cm" });
    const [selectedHeightUnit, setSelectedHeightUnit] = useState({ "id": "cm", "label": "cm" });
    const [stackMultiplier] = useState([{ "id": 2, "label": "Y" }, { "id": 0, "label": "N" }]);
    const [showStackable, setShowStackable] = useState(false);
    const [showLCLLengthMsg, setLCLLengthMsg] = useState(true);
    const [showLCLHeightMsg, setLCLHeightMsg] = useState(true);
    const { setAddedPackages } = useContext(PackageContext);

    const styleWarn = {
        backgroundColor: 'rgba(255, 255, 0, 0.1)'
    }

    const styleNormal = {
        backgroundColor: 'white'
    }

    useEffect(() => {
        setAddedPackages(packages);
    }, [packages, setAddedPackages]);

    useEffect(() => {
        // Populate measurement units and package types
        const getMeasurementData = async () => {
            try {
                // Populate package types
                await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/lookup/GetPackageTypes`, {
                    params: {
                        cargoMode: cargoMode,
                        primaryTransportMode: primaryTransportMode
                    }
                })
                    .then((res) => setPackageTypes(res.data))

                // Populate weight measurement units
                await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/lookup/GetMeasureUnitsWeight`)
                    .then((res) => setMeasureUnitsWeight(res.data))

                // Populate length measurement units
                await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/lookup/GetMeasureUnitsLength`)
                    .then((res) => setMeasureUnitsLength(res.data))

                // Populate width measurement units
                await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/lookup/GetMeasureUnitsWidth`)
                    .then((res) => setMeasureUnitsWidth(res.data))

                // Populate height measurement units
                await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/lookup/GetMeasureUnitsHeight`)
                    .then((res) => setMeasureUnitsHeight(res.data))
            } catch (error) {
                if(error.response){
                    console.error(`An error occured whilst getting measurement data. Status: ${error.response.status}. Detail from server: ${error.response.data.detail}.`);
                }

            }
        }

        getMeasurementData();

    }, [cargoMode, primaryTransportMode])

    useEffect(() => {
        if (cargoMode !== CargoMode.FCL) {
            setShowStackable(true);
        }

        if (cargoMode === CargoMode.LCL) {
            setLCLLengthMsg(true);
            setLCLHeightMsg(true);
        }
        else {
            setLCLLengthMsg(false);
            setLCLHeightMsg(false);
        }
    }, [cargoMode]);

    //Handles the changes to each of the columns in the rows
    const handleChange = (index, event) => {
        let { name, value } = event.target;
        if (name !== undefined) {
            name = name.replace(/[0-9]/g, '');
            var matches = name.match(/\d+/g);
            if (matches !== null) {
                console.log("There was an issue with updating the package line.");
            }

            const updatedPackages = [...packages];
            Object.assign(packages[index], { [name]: value })
            setPackages(updatedPackages);
        }

        if (cargoMode === CargoMode.LCL) {
            if (name.includes("length")) {
                if (value < 201) {
                    setLCLLengthMsg(false);
                }
                else {
                    setLCLLengthMsg(true);
                }
            }

            if (name.includes("height")) {
                if (value < 201) {
                    setLCLHeightMsg(false);
                }
                else {
                    setLCLHeightMsg(true);
                }
            }
        }
        else {
            setLCLLengthMsg(false);
            setLCLHeightMsg(false);
        }
    };

    const handleStackableChange = (value, index) => {
        if (value) {
            setPackages((prevPackages) =>
                prevPackages.map((item, i) => i === index
                    ? { ...item, 'stackMultiplier': value.id }
                    : item
                )
            );
        }
    }

    //Handles a new row being added
    const handleAddRow = () => setPackages([...packages, newPackage]);

    //Handles the change of the package type dropdowns
    const handlePackageType = async (value, index) => {
        try {
            await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/lookup/GetPackageTypeDetails`, {
                params: {
                    packageTypeId: value.id
                }})
                .then((res) => {
                    setPackages((prevPackages) =>
                        prevPackages.map((item, i) =>
                            i === index
                                ? {
                                    ...item,
                                    'type': value.id,
                                    'height': Math.round(res.data[0].standardHeight),
                                    'width': Math.round(res.data[0].standardWidth),
                                    'length': Math.round(res.data[0].standardLength)}
                                : item
                        )
                    );
                })
        } catch (error) {
            if(error.response){
                console.error(`An error occured whilst getting package type data. Status: ${error.response.status}. Detail from server: ${error.response.data.detail}.`);
            }
        }
    }

    //Handles the change of the measure units for the conversions
    const handleMeasureUnits = async (value) => {
        const unitType = value.name;
        const newUnit = value.id;

        const selectedUnits = {
            'grossweight': [selectedWeightUnit, setSelectedWeightUnit],
            'length': [selectedLengthUnit, setSelectedLengthUnit],
            'width': [selectedWidthUnit, setSelectedWidthUnit],
            'height': [selectedHeightUnit, setSelectedHeightUnit]
        }

        selectedUnits[unitType][1](value);

        if (unitType in selectedUnits) {
            for (let i = 0; i < packages.length; i++) {
                const currentUnit = selectedUnits[unitType][0].id;
                const unitValue = packages[i][unitType];
                const convertedMeasurementValue = await convertMeasurementValue(currentUnit, newUnit, unitValue);

                updateMeasurementAtIndex(i, convertedMeasurementValue, unitType, newUnit);
                updateMeasurementUnits(unitType, newUnit);
            }
        }
    }


    const updateMeasurementAtIndex = (index, convertedMeasurementValue, unitType, newUnit) => {
        setPackages((prevPackages) =>
            prevPackages.map((item, packageIndex) =>
                index === packageIndex ? { ...item, [unitType]: convertedMeasurementValue } : item
            )
        );
    }

    // Updates measurement units for all packages in state, ideally the naming needs to be changed so that it's all consistent.
    const updateMeasurementUnits = (unitType, newUnit) => {
        const selectedUnits = {
            'grossweight': 'measureUnitsGrossWeight',
            'length': 'measureUnitsLength',
            'width': 'measureUnitsWidth',
            'height': 'measureUnitsHeight'
        }

        setPackages((prevPackages) =>
            prevPackages.map((item) =>
                ({...item, [selectedUnits[unitType]]: newUnit })
            )
        );
    }

    const convertMeasurementValue = async (currentUnit, newUnit, unitValue) => {
        try {
            const covertedMeasurement = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/lookup/GetMeasurementConvertor`, {
                params: {
                    fromMeasure: currentUnit,
                    toMeasure: newUnit,
                    decimalValue: unitValue
                }
            })
                .then((res) => res.data)
            return covertedMeasurement;
        } catch (error) {
            if(error.response){
                console.error(`An error occured whilst converting a measurement. Status: ${error.response.status}. Detail from server: ${error.response.data.detail}.`);
            }
        }
    }

    const deleteRow = (index) => {
        if (packages.length > 1) {
            let newPackages = [...packages];
            newPackages.splice(index, 1);
            setPackages(newPackages);
        }
    };

    const renderPackageRows = () => {
        return (
            packages.map((item, index) => (
                <tr id="addr0" key={index}>
                    <td>
                        <TextField
                            size="small"
                            type="number"
                            label=" "
                            InputLabelProps={{ shrink: false }}
                            helperText={!item.packageCountValidation ? "Incorrect entry.": "" }
                            name={"packageCount" + index}
                            value={item.packageCount}
                            onChange={(e) => handleChange(index, e)}
                            error={!item.packageCountValidation}
                            sx={{
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                    display: "none",
                                },
                                "& input[type=number]": {
                                    MozAppearance: "textfield",
                                },
                            }} />
                    </td>
                    <td>
                        <Autocomplete
                            size="small"
                            disablePortal
                            name={"type" + index}
                            options={packageTypes}
                            onChange={(_, value) => handlePackageType(value, index)}
                            defaultValue={item.packageTypeDescription}
                            renderInput={(params) => <TextField size="small" {...params} name={"type" + index} label="Please Select" error={!item.typeValidation} helperText={!item.typeValidation ? "Incorrect entry." : ""} />} />
                    </td>
                    <td>
                        <TextField
                            size="small"
                            type="text"
                            label=" "
                            InputLabelProps={{ shrink: false }}
                            helperText={!item.descriptionValidation ? "Incorrect entry." : ""}
                            name={"description" + index}
                            value={packages[index].index}
                            defaultValue={packages[index].description}
                            onChange={(e) => handleChange(index, e)}
                            error={!item.descriptionValidation}
                        />
                    </td>
                    <td>
                        <TextField
                            size="small"
                            type="number"
                            label=" "
                            InputLabelProps={{ shrink: false }}
                            helperText={!item.grossweightValidation ? "Incorrect entry." : ""}
                            id={"grossweight" + index}
                            name={"grossweight" + index}
                            value={packages[index].grossweight}
                            onChange={(e) => handleChange(index, e)}
                            defaultValue={packages[index].grossweight}
                            error={!item.grossweightValidation}
                            sx={{
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                    display: "none",
                                },
                                "& input[type=number]": {
                                    MozAppearance: "textfield",
                                },
                            }} />
                    </td>
                    <td>
                        <TextField
                            size="small"
                            type="number"
                            label=" "
                            InputLabelProps={{ shrink: false }}
                            helperText={!item.lengthValidation ? "Incorrect entry." : ""}
                            id={"length" + index}
                            name={"length" + index}
                            value={packages[index].length}
                            onChange={(e) => handleChange(index, e)}
                            defaultValue={packages[index].length}
                            error={!item.lengthValidation}
                            sx={{
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                    display: "none",
                                },
                                "& input[type=number]": {
                                    MozAppearance: "textfield",
                                },
                            }} />
                    </td>
                    <td>
                        <TextField
                            size="small"
                            type="number"
                            label=" "
                            InputLabelProps={{ shrink: false }}
                            helperText={!item.widthValidation ? "Incorrect entry." : ""}
                            id={"width" + index}
                            name={"width" + index}
                            value={packages[index].width}
                            onChange={(e) => handleChange(index, e)}
                            defaultValue={packages[index].width}
                            error={!item.widthValidation}
                            sx={{
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                    display: "none",
                                },
                                "& input[type=number]": {
                                    MozAppearance: "textfield",
                                },
                            }} />
                    </td>
                    <td>
                        <TextField
                            size="small"
                            type="number"
                            label=" "
                            InputLabelProps={{ shrink: false }}
                            helperText={!item.heightValidation ? "Incorrect entry." : ""}
                            id={"height" + index}
                            name={"height" + index}
                            value={packages[index].height}
                            onChange={(e) => handleChange(index, e)}
                            defaultValue={packages[index].height}
                            error={!item.heightValidation}
                            sx={{
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                    display: "none",
                                },
                                "& input[type=number]": {
                                    MozAppearance: "textfield",
                                },
                            }} />
                    </td>
                    <td>
                        <TextField
                            size="small"
                            type="number"
                            label=" "
                            InputLabelProps={{ shrink: false }}
                            helperText={!item.volumeValidation ? "Incorrect entry." : ""}
                            id={"volume" + index}
                            name={"volume" + index}
                            value={packages[index].volume}
                            onChange={(e) => handleChange(index, e)}
                            defaultValue={packages[index].volume}
                            error={!item.volumeValidation}
                            sx={{
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                    display: "none",
                                },
                                "& input[type=number]": {
                                    MozAppearance: "textfield",
                                },
                            }} />
                    </td>
                    {showStackable && (<td>
                        <Autocomplete
                            size="small"
                            disablePortal
                            name={"stackMultiplier" + index}
                            options={stackMultiplier}
                            onChange={(_, value) => handleStackableChange(value, index)}
                            defaultValue={stackMultiplier[1]}
                            renderInput={(params) => <TextField size="small" {...params} name={"stackMultiplier" + index} label="Please Select" />}
                        />
                    </td>)}
                    <td>
                        <CancelIcon onClick={() => deleteRow(index)}
                            sx={{
                                fontSize: 40,
                                color: backgroundColor,
                                cursor: 'pointer'
                            }}
                        />
                    </td>
                </tr>
            ))
        )
    };

    const renderPackages = renderPackageRows();

    return (
        <div>
            {/* Change this */}
            <input type="hidden" name="rowCount" id="rowCount" value={packages.length} className="form-control" />
            <table
                className="table table-bordered table-hover"
                id="tab_logic"
            >
                <thead>
                    <tr>
                        <th className="text-center"> Quantity </th>
                        <th className="text-center" style={{ width: '170px' }}> Type </th>
                        <th className="text-center" style={{ width: '170px' }}> Description </th>
                        <th className="text-center"> Total Gross Weight
                            <Autocomplete
                                size="small"
                                style={{ width: '80px' }}
                                disablePortal
                                name={"measureUnitsGrossWeight"}
                                defaultValue={"kg"}
                                options={measureUnitsWeight}
                                onChange={(_, value) => handleMeasureUnits(value)}
                                renderInput={(params) => <TextField size="small" {...params} name={"measureUnitsGrossWeight"} label="" />}
                            />
                        </th>
                        <th className="text-center" style={showLCLLengthMsg ? styleWarn : styleNormal}> Length
                            <Autocomplete
                                size="small"
                                style={{ width: '80px' }}
                                disablePortal
                                name={"measureUnitsLength"}
                                defaultValue={"cm"}
                                options={measureUnitsLength}
                                onChange={(_, value) => handleMeasureUnits(value)}
                                renderInput={(params) => <TextField size="small" {...params} name={"measureUnitsLength"} label="" />}
                            />
                        </th>
                        <th className="text-center"> Width
                            <Autocomplete
                                size="small"
                                style={{ width: '80px' }}
                                disablePortal
                                name={"measureUnitsWidth"}
                                defaultValue={"cm"}
                                options={measureUnitsWidth}
                                onChange={(_, value) => handleMeasureUnits(value)}
                                renderInput={(params) => <TextField size="small" {...params} name={"measureUnitsWidth"} label="" />}
                            />
                        </th>
                        <th className="text-center" style={showLCLHeightMsg ? styleWarn : styleNormal}> Height
                            <Autocomplete
                                size="small"
                                style={{ width: '80px' }}
                                disablePortal
                                name={"measureUnitsHeight"}
                                defaultValue={"cm"}
                                options={measureUnitsHeight}
                                onChange={(_, value) => handleMeasureUnits(value)}
                                renderInput={(params) => <TextField size="small" {...params} name={"measureUnitsHeight"} label="" />}
                            />

                        </th>
                        <th className="text-center" style={{ width: '80px' }}> Volume (cmb) </th>
                        {showStackable && (<th className="text-center" style={{ width: '100px' }}> Stackable </th>)}
                        <th> <DeleteIcon className="text-center" /> </th>
                    </tr>
                </thead>
                <tbody>
                    {renderPackages}
                </tbody>
            </table>
            {showLCLLengthMsg && (<p>This quote will be invalid if
                a LCL package component is longer than 200cm in length for sea. </p>
            )}
            {showLCLHeightMsg && (<p>This quote will be invalid if
                a LCL package component is higher than 160cm in height for air. </p>
            )}
            <Button onClick={handleAddRow} style={{ backgroundColor: backgroundColor }}>
                Add Row
            </Button>
        </div>
    );

}
